import { Component, inject } from "@angular/core";
import { RouterOutlet } from "@angular/router";
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";
import { setPseudoEnabled, writeCachedLanguage } from "./shared/i18n/lang-override";
import { I18nMissCatcherService } from "./i18n-miss-catcher/i18n-miss-catcher.service";
import { ClientConfigService } from "./shared/services/client-config.service";

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
    imports: [RouterOutlet],
})
export class AppComponent {
    private matIconRegistry = inject(MatIconRegistry);
    private domSanitizer = inject(DomSanitizer);

    private readonly clientConfigService = inject(ClientConfigService);

    constructor() {
        this.registerMsIcons();
        this.registerCustomIcons();

        if (this.clientConfigService.i18nToolingEnabled) {
            // Dev-only console affordance: `cerrix.setLang('pseudo')` / `cerrix.setLang('en')`.
            // Like a real language switch (ADR-010) it persists the choice and reloads, so the
            // bootstrap applies it. A real language only seeds the pre-login cache: once the
            // user is known, the bootstrap re-resolves their own language.
            (window as unknown as { cerrix: { setLang(lang: string): void } }).cerrix = {
                setLang: (lang: string) => {
                    setPseudoEnabled(lang === "pseudo");
                    if (lang !== "pseudo") {
                        writeCachedLanguage(lang);
                    }
                    this.reloadPage();
                },
            };
            inject(I18nMissCatcherService).bootstrap();
        }
    }

    // Extracted so specs can spy on it: window.location.reload is not spyable in place.
    protected reloadPage(): void {
        window.location.reload();
    }

    private registerMsIcons(): void {
        const productIconLoc = "api/icons/product/";

        this.registerIcon(`${productIconLoc}word_48x1`, "ms-word");
        this.registerIcon(`${productIconLoc}excel_48x1`, "ms-excel");
        this.registerIcon(`${productIconLoc}powerpoint_48x1`, "ms-powerpoint");

        // Register Microsoft Office file icons
        const msFileIconLoc = "api/icons/document/";

        // Some of these do not have a file icon, so we will fall back to the generic one via the backend.
        const msFileExtions = [
            "doc",
            "docx",
            "docm",
            "dot",
            "dotm",
            "dotx",
            "odt",
            "odp",
            "pot",
            "potm",
            "potx",
            "pps",
            "ppsm",
            "ppsx",
            "ppt",
            "pptm",
            "pptx",
            "csv",
            "ods",
            "xls",
            "xlsm",
            "xlsx",
        ];

        for (const fileExt of msFileExtions) {
            this.registerIcon(`${msFileIconLoc}${fileExt}_48x1`, `ms-${fileExt}`);
        }
    }

    private registerCustomIcons() {
        const icons = [
            "add-list",
            "add-preset",
            "add-user",
            "add",
            "add-user",
            "arrow-down",
            "arrow-left",
            "arrow-right-alt",
            "arrow-right",
            "arrow-up",
            "attachments",
            "browse-preset",
            "calendar",
            "chat",
            "check",
            "check-circle",
            "checkbox-check",
            "checkbox-indeterminate",
            "clock",
            "close",
            "cloud",
            "collapse",
            "comment",
            "delete",
            "document",
            "download",
            "duplicate",
            "edit-2",
            "edit",
            "expand",
            "export",
            "fa-chart-line",
            "fa-cloud-upload-alt",
            "fa-copy",
            "fa-download",
            "fa-dumpster-fire",
            "fa-file-archive",
            "fa-file-excel",
            "fa-history",
            "fa-share-alt",
            "fa-tags",
            "filter-alt",
            "filter",
            "generate-ai",
            "globe",
            "hide",
            "history",
            "instructions",
            "internal-organization",
            "kebab-menu",
            "link",
            "link-external",
            "linked-attachement",
            "linked-items",
            "metrics",
            "moi's",
            "multiselect",
            "open-external",
            "open-list",
            "pin",
            "refresh",
            "reload",
            "sample-remark",
            "save-preset",
            "save",
            "search",
            "send-message",
            "setting",
            "show",
            "single-user",
            "slide-open",
            "soft-warning",
            "third-party",
            "unlink",
            "up-down",
            "update-preset",
            "upload-cloud",
            "view-file",
            "warning",
        ];

        for (const icon of icons) {
            this.registerIcon(`assets/icons/cerrix/${icon}.svg`, icon);
        }
    }

    private registerIcon(iconLoc: string, iconName: string): void {
        const safeUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(iconLoc);
        this.matIconRegistry.addSvgIcon(iconName, safeUrl);
    }
}
